import React, { useRef, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  getTimelineChartOptions,
  formatDataLayerGuardChartData,
} from "./chartUtils";
import { formatNumber } from "../../utils";
import ChartNoData from "../ChartNoData";
import "./DataLayerGuardChart.scss";

/**
 * DataLayer Guard Event Errors Timeline Chart
 * Displays error counts over time with color-coded event tracking
 */
const DataLayerGuardChart = ({
  timelineData = [],
  title = "Event Errors Over Time",
  height = 350,
  loading = false,
  onDataPointClick = null,
}) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  // Format data for Chart.js
  const chartData = useMemo(() => {
    return formatDataLayerGuardChartData(timelineData);
  }, [timelineData]);

  // Chart options with custom styling
  const chartOptions = useMemo(() => {
    const baseOptions = getTimelineChartOptions(title);

    return {
      ...baseOptions,
      onClick: (event, elements) => {
        if (elements.length > 0 && onDataPointClick) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const dataIndex = element.index;
          const dataset = chartData.datasets[datasetIndex];
          const label = chartData.labels[dataIndex];
          const value = dataset.data[dataIndex];

          onDataPointClick({
            eventName: dataset.label,
            timestamp: label,
            errorCount: value,
            datasetIndex,
            dataIndex,
          });
        }
      },
      onHover: (event, elements) => {
        const chart = chartRef.current;
        if (chart && chart.canvas) {
          chart.canvas.style.cursor =
            elements.length > 0 ? "pointer" : "default";
        }
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          ...baseOptions.plugins.tooltip,
          callbacks: {
            title: (context) => {
              return `Date: ${context[0].label}`;
            },
            label: (context) => {
              const errorCount = context.parsed.y;
              return `${context.dataset.label}: ${formatNumber(errorCount)} error${errorCount !== 1 ? "s" : ""}`;
            },
          },
        },
        legend: {
          ...baseOptions.plugins.legend,
          onClick: (e, legendItem, legend) => {
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            const isVisible = chart.isDatasetVisible(index);

            if (isVisible) {
              chart.hide(index);
            } else {
              chart.show(index);
            }

            chart.update();
          },
        },
      },
    };
  }, [title, chartData, onDataPointClick]);

  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="datalayer-chart-container" ref={containerRef}>
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading chart data...</p>
        </div>
      </div>
    );
  }

  // Without errors the lines at 0 are the answer, the "no errors" message is in the Top 5 errors next to it
  const hasData = timelineData?.length > 0;

  return (
    <div className="datalayer-chart-container" ref={containerRef}>
      <ChartNoData show={!hasData} dataName="DataLayer Guard">
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </ChartNoData>
    </div>
  );
};

export default DataLayerGuardChart;
